import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { TYPES } from '../../utils/constants';
import ProductCard from '../ui/ProductCard';

/**
 * ProductCollection
 *
 * Props:
 *  - onAddToCart: function
 *  - category (optional): 'men' | 'women' | 'unisex' | 'all'
 *    Despite the name, this is a gender filter, not a product category.
 *    The full catalog is always fetched via /api/products/all and then
 *    filtered client-side by each product's own gender field.
 *  - sale (optional): boolean — reserved for future sale filtering
 *
 * Server filename format:
 *   id - price - name - category - type - [colors] - color
 *   [0]   [1]    [2]      [3]      [4]      [5]       [6]
 */
const ProductCollection = ({ onAddToCart, category, sale }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(TYPES.ALL);
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('search')?.trim() || '';

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    setActiveTab(TYPES.ALL);
  }, [category]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);

        // Always fetch the full catalog and filter client-side. The
        // backend's /api/products endpoint expects a real product
        // category (scrub_top, scrub_set, etc.) plus a gender — it isn't
        // built for "show me everything for one gender," which is what
        // this component's `category` prop (all/women/men) actually
        // represents. /api/products/all sidesteps that mismatch entirely.
        const response = await fetch(`${BACKEND_URL}/api/products/all`);

        if (!response.ok) {
          throw new Error('Failed to fetch products');
        }

        const data = await response.json();

        console.log('Products fetched:', data);
        setProducts(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching products:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [BACKEND_URL]);

  // Filter by gender (the `category` prop — despite the name, it's really
  // 'all' | 'women' | 'men' | 'unisex' — matched against each product's own
  // parsed gender field), then by the active type tab, then by a free-text
  // search term (matched against the product name) if one is present in
  // the URL — e.g. from the header search bar submitting to /all?search=...
  const filteredProducts = products
    .filter(product => !category || category === 'all' || product.gender === category)
    .filter(product => activeTab === TYPES.ALL || product.type === activeTab)
    .filter(product =>
      !searchQuery || (product.name || '').toLowerCase().includes(searchQuery.toLowerCase())
    );

  // Each color of a product is a separate S3 file/entry, so without this
  // the grid would show one card per color instead of one per product.
  // groupKey requires name/price/gender/category/type/colors to match
  // byte-for-byte across every color file, which is easy to break with a
  // stray space or inconsistent capitalization when naming files by hand —
  // so we dedupe on a normalized name+gender+category+type instead, which
  // is more forgiving of that kind of noise. Keep the first color variant
  // encountered as the representative card; the product page itself still
  // offers every color once the person opens it.
  const normalize = (value) => (value || '').toString().trim().toLowerCase();
  const dedupeKey = (product) =>
    [product.name, product.gender, product.category, product.type].map(normalize).join('|');

  const displayProducts = Array.from(
    new Map(filteredProducts.map(product => [dedupeKey(product), product])).values()
  );

  const tabs = [
    // { key: TYPES.ALL,          label: 'All'                  },
    // { key: TYPES.SCRUB_TOP,    label: 'Scrub Tops'           },
    // { key: TYPES.SCRUB_PANT,   label: 'Scrub Pants'          },
    // { key: TYPES.SCRUB_SET,    label: 'Scrub Sets'           },
    // { key: TYPES.WHITE_COAT,   label: 'White Coats'          },
    // { key: TYPES.SCRUB_JACKET, label: 'Daily Scrub Jackets'  },
  ];

  if (loading) {
    return (
      <section className="py-16 bg-navy">
        <div className="container mx-auto px-4 text-center">
          <p className="text-gray-600">Loading products...</p>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-16 bg-navy">
        <div className="container mx-auto px-4 text-center">
          <p className="text-red-600">Error: {error}</p>
        </div>
      </section>
    );
  }

  return (
    <section id="shop-collection" className="py-16 bg-navy">
      <div className="container mx-auto px-4">
        {/* Only show section heading on the landing page */}
        {!category && !searchQuery && (
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">Our Collection</h2>
        )}

        {searchQuery && (
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">
            Search results for "{searchQuery}"
          </h2>
        )}

        {/* Type Tabs */}
        <div className="flex justify-center mb-12 overflow-x-auto">
          <div className="inline-flex border-b border-primary/30">
            {tabs.map(tab => (
              <button
                key={tab.key}
                disabled={tab.key === TYPES.SCRUB_JACKET}
                onClick={() => setActiveTab(tab.key)}
                className={`
                  px-6 py-3 font-medium text-lg whitespace-nowrap transition duration-300
                  ${tab.key === TYPES.SCRUB_JACKET
                    ? 'text-red-600 cursor-not-allowed opacity-60'
                    : activeTab === tab.key
                      ? 'text-primary border-b-2 border-primary'
                      : 'text-gray-500 hover:text-primary'
                  }
                `}
              >
                {tab.key === TYPES.SCRUB_JACKET
                  ? `${tab.label} (Coming Soon)`
                  : tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {displayProducts.length > 0 ? (
            displayProducts.map(product => (
              <ProductCard
                key={`${product.id}-${product.color}`}
                product={product}
                onAddToCart={onAddToCart}
              />
            ))
          ) : (
            <div className="col-span-4 text-center py-8">
              <p className="text-gray-500">
                {searchQuery
                  ? `No products found for "${searchQuery}".`
                  : 'No products found in this category.'}
              </p>
            </div>
          )}
        </div>

        {/* View All Products */}
        {!category && (
          <div className="text-center mt-12">
            <Link
              to="/all"
              className="inline-block bg-transparent border border-primary text-primary hover:bg-primary hover:text-white font-medium py-3 px-8 rounded-md transition duration-300"
            >
              View All Products
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default ProductCollection;